import { useEffect, useId, useRef, useState } from "react";
import { onLinkClick, type Route } from "../hooks/useRouter.ts";
import { config } from "../lib/firebase.ts";
import { useApp } from "../state/AppState.tsx";
import { BookmarkIcon } from "./ui.tsx";
import { shortcutLabel } from "./CommandPalette.tsx";
import { FEATURES } from "../lib/features.ts";
import { useTheme } from "../lib/theme.ts";

export function Header({ route, onSearch }: { route: Route; onSearch(): void }) {
  const app = useApp();
  const count = app.basket.length;
  const navLink = (href: string, label: string, r: Route, short = label) => (
    <a
      href={href}
      onClick={onLinkClick}
      aria-current={route === r ? "page" : undefined}
      className={`inline-flex min-h-11 items-center whitespace-nowrap rounded-lg px-2.5 text-sm font-medium transition-colors sm:px-3 ${route === r ? "bg-sand text-ink" : "text-ink-soft hover:text-ink"}`}
    >
      <span className="sm:hidden">{short}</span>
      <span className="hidden sm:inline">{label}</span>
    </a>
  );

  return (
    <header className="sticky top-0 z-30 border-b border-line bg-paper/90 backdrop-blur supports-[backdrop-filter]:bg-paper/75">
      <div className="mx-auto flex min-h-14 max-w-[90rem] flex-wrap items-center gap-x-2 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
        <a href="/" onClick={onLinkClick} aria-label="Databricks Pulse home" className="mr-1 flex shrink-0 items-center gap-2 whitespace-nowrap font-display text-base font-bold tracking-tight sm:mr-2">
          <img src="/favicon.svg" alt="" className="size-7" />
          <span className="hidden min-[440px]:inline"><span className="hidden sm:inline">Databricks </span><span className="text-coral-deep">Pulse</span></span>
        </a>
        <nav aria-label="Main" className="order-last -mx-1.5 flex w-full items-center gap-1 overflow-x-auto pb-1 sm:order-none sm:mx-0 sm:w-auto sm:pb-0">
          {FEATURES.digest && navLink("/", "Digest", "digest")}
          {navLink(FEATURES.digest ? "/browse" : "/", "Browse", "discover")}
          {navLink("/map", "Map", "map")}
          {navLink("/glossary", "Glossary", "glossary")}
          {navLink("/roundup", "Weekly roundup", "roundup", "Roundup")}
          {navLink("/trends", "Trends", "trends")}
          {FEATURES.briefing && navLink("/briefing", "My Briefing", "briefing", "Briefing")}
        </nav>
        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          <button
            type="button"
            onClick={onSearch}
            aria-keyshortcuts="Control+K Meta+K"
            aria-label="Search all updates"
            className="inline-flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-lg border border-line-strong bg-card px-3 text-sm text-ink-soft hover:border-ink/50 hover:text-ink"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="6.5" /><path d="m20 20-4-4" /></svg>
            <span className="hidden md:inline">Search</span>
            <kbd className="hidden rounded border border-line px-1 text-2xs md:inline">{shortcutLabel()}</kbd>
          </button>
          {/* Mobile/tablet: Basket lives in the header so nothing floats over content. */}
          <button
            type="button"
            onClick={() => app.setBasketOpen(true)}
            aria-haspopup="dialog"
            aria-label={`Reading Basket, ${count} saved`}
            className="relative inline-grid size-11 place-items-center rounded-lg border border-line-strong bg-card text-ink hover:border-ink/50 lg:hidden"
          >
            <BookmarkIcon filled={count > 0} />
            {count > 0 && (
              <span aria-hidden="true" className="absolute -right-1 -top-1 min-w-5 rounded-full bg-ink px-1 text-center text-2xs font-semibold leading-5 text-on-ink">
                {count}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => app.setBasketOpen(!app.basketOpen)}
            aria-expanded={app.basketOpen}
            aria-controls="reading-basket"
            className="hidden min-h-11 items-center gap-2 rounded-lg border border-line-strong bg-card px-3 text-sm font-semibold hover:border-ink/50 lg:inline-flex"
          >
            <BookmarkIcon filled={count > 0} />
            Reading Basket
            <span className="min-w-6 rounded-full bg-ink px-1.5 text-center text-xs leading-5 text-on-ink" aria-label={`${count} saved`}>
              {count}
            </span>
          </button>
          <Account />
          <ThemeToggle />
          <About />
        </div>
      </div>
      {config.mode !== "production" && (
        <p className="border-t border-line bg-sand px-4 py-1 text-center text-xs leading-snug text-ink">
          {config.mode === "sample"
            ? "Sample mode: bundled fictional stories only, no database."
            : "Local preview: data from your local emulator."}
        </p>
      )}
    </header>
  );
}

/** Sun/moon button: switches between light and dark and remembers the choice on this browser. */
function ThemeToggle() {
  const [theme, toggle] = useTheme();
  const dark = theme === "dark";
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
      title={dark ? "Light theme" : "Dark theme"}
      className="inline-grid size-11 place-items-center rounded-full border border-line-strong bg-card text-ink-soft transition-colors hover:border-ink/50 hover:text-ink"
    >
      {dark ? (
        <svg aria-hidden="true" viewBox="0 0 24 24" className="size-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2.5v2M12 19.5v2M4.6 4.6l1.4 1.4M18 18l1.4 1.4M2.5 12h2M19.5 12h2M4.6 19.4 6 18M18 6l1.4-1.4" />
        </svg>
      ) : (
        <svg aria-hidden="true" viewBox="0 0 24 24" className="size-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" />
        </svg>
      )}
    </button>
  );
}

const LINKS = [
  { href: "https://www.linkedin.com/in/ashraf-osman-phd", label: "Ashraf Osman", icon: "linkedin" },
  { href: "https://www.databricks.com", label: "Databricks", icon: "databricks" },
] as const;

/** "i" button, top right: what Pulse is, who made it, and the build version. Closes on Escape or a click outside. */
function About() {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !root.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      <button
        ref={button}
        type="button"
        aria-label="About Databricks Pulse"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className={`inline-grid size-11 place-items-center rounded-full border transition-colors ${open ? "border-coral-deep bg-coral-wash text-coral-deep" : "border-line-strong bg-card text-ink-soft hover:border-ink/50 hover:text-ink"}`}
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" className="size-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 11v5.5M12 7.6v.1" />
        </svg>
      </button>
      {open && (
        <div id={panelId} role="dialog" aria-labelledby={titleId} className="absolute right-0 top-full z-40 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-xl border border-line bg-card p-4 shadow-lg">
          <h2 id={titleId} className="text-base font-semibold">About</h2>
          <p className="mt-2 text-sm leading-relaxed text-ink-soft">
            Every official Databricks release note, blog post and press release in one place, with short summaries, a product map, weekly roundups and reader trends. An independent reader, not an official Databricks site.
          </p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {LINKS.map((l) => (
              <li key={l.href}>
                <a href={l.href} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-10 items-center gap-2 rounded-full border border-line-strong px-3.5 text-sm font-semibold text-ink hover:bg-sand">
                  {l.icon === "linkedin" ? <LinkedInIcon /> : <CubeIcon />}
                  {l.label}
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-ink-soft">{import.meta.env.VITE_APP_VERSION}</p>
        </div>
      )}
    </div>
  );
}

function LinkedInIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="size-4" fill="currentColor">
      <path d="M4 3h16a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm2.5 7v7.5h2.6V10H6.5Zm1.3-4.1a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm3.5 4.1v7.5h2.6v-3.9c0-1 .2-2 1.5-2s1.3 1.2 1.3 2.1v3.8h2.6v-4.3c0-2.2-.5-3.9-3-3.9-1.2 0-2 .6-2.4 1.2V10h-2.6Z" />
    </svg>
  );
}

function CubeIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
      <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" />
      <path d="m4 7.5 8 4.5 8-4.5M12 12v9" />
    </svg>
  );
}

function Account() {
  const app = useApp();
  if (!FEATURES.signIn || !app.authAvailable) return null;
  if (!app.user)
    return (
      <button type="button" onClick={() => void app.signIn()} aria-label="Sign in with Google" className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg p-2 text-sm font-semibold text-coral-deep hover:bg-coral-wash sm:px-3 sm:py-1.5">
        <PersonIcon />
        <span className="hidden sm:inline">Sign in with Google</span>
      </button>
    );
  const name = app.user.displayName || app.user.email || "Account";
  return (
    <div className="flex items-center gap-2">
      <span className="hidden max-w-40 truncate text-sm text-ink-soft sm:inline" title={name}>{name}</span>
      <button type="button" onClick={() => void app.signOutUser()} aria-label={`Sign out ${name}`} className="whitespace-nowrap rounded-lg px-2 py-1.5 sm:px-3 text-sm font-medium text-ink-soft hover:bg-sand hover:text-ink">
        Sign out
      </button>
    </div>
  );
}

function PersonIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="size-[18px] sm:hidden" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M5 19.5c1.2-3.4 3.8-5 7-5s5.8 1.6 7 5" />
    </svg>
  );
}
